import React, { useState, useContext, useRef } from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { StructuredListing } from '@hz-design-system/web-ui';
import { formatImageUrl, ImageSize } from '@ecg-marktplaats/media-util-js-lib';
import EnvironmentContext from '../../../../contexts/EnvironmentContext';
import { trackGAEvent } from '../../../../utils/gaTracking';
import { getSessionStorageItem, setSessionStorageItem } from '../../../../utils/sStorage';
import formatPrice from './utils/formatPrice';
import { TFeedItem } from 'homepage-web-bff/types/TFeeds';
import deleteFavorite from '../../repositories/deleteFavorite';
import createFavorite from '../../repositories/createFavorite';
import showToastNotification, { dismissToast } from '../Toast/ToastNotification';
import BuyerInfoModal from '../FavouritesConnect/BuyerInfoModal';

type TFeedItemProps = {
  item: TFeedItem;
  lazyLoadImage: boolean;
  onClick: Function;
  feedType: string;
  feedPostCode?: string;
  setShowLoginDialog: Function;
  className?: string;
};

const updateFeeds = (item: TFeedItem, feedType: string, isFavorited: boolean, feedPostCode?: string) => {
  const storageKey = feedPostCode ? `HOMEPAGE_FEED_${feedType}_${feedPostCode}` : `HOMEPAGE_FEED_${feedType}`;
  const cachedData = getSessionStorageItem(storageKey);
  if (cachedData?.feedItems) {
    const updatedFeeds = cachedData.feedItems.map((feed) => {
      return feed.itemId === item.itemId ? { ...feed, isFavorited } : feed;
    });
    setSessionStorageItem(storageKey, {
      feedItemsPage: cachedData?.feedItemsPage,
      feedItems: updatedFeeds,
    });
  }
};

const FeedItem = ({
  item,
  lazyLoadImage,
  onClick,
  feedType,
  feedPostCode,
  setShowLoginDialog,
  className,
}: TFeedItemProps) => {
  const { t } = useI18nContext();
  const { xsrfToken, userLoggedIn, experiments = {}, favouritesConnect = {} } = useContext(EnvironmentContext);

  const [url, setUrl] = useState(item.url);
  const [isFavorite, setIsFavorite] = useState(item.isFavorited ? item.isFavorited : false);
  const [showFavouriteInfoModal, setShowFavouriteInfoModal] = useState(false);

  const closeToast = useRef<any>(null);

  const { isFavouritesConnectEnabled } = experiments;
  const imageLoading = lazyLoadImage ? 'lazy' : undefined;
  const imageDecoding = lazyLoadImage ? 'async' : undefined;

  const validateResoponse = (response, isFav: boolean) => {
    if (closeToast.current) {
      closeToast.current();
    }
    closeToast.current = dismissToast;

    if (response && 'isFavorite' in response && isFav !== response.isFavorite) {
      showToastNotification({
        type: 'success',
        description: t(
          response.isFavorite
            ? 'homepage.listing.favourite.notifications.favoriting'
            : 'homepage.listing.favourite.notifications.unfavoriting',
        ),
      });

      if (response.isFavorite) {
        trackGAEvent(
          'WatchlistAddSuccess',
          undefined,
          isFavouritesConnectEnabled
            ? {
                // eslint-disable-next-line camelcase
                seller_contact_enabled: favouritesConnect.sellerContactEnabled,
              }
            : undefined,
        );
      } else {
        trackGAEvent('WatchlistRemoveSuccess');
      }
      return true;
    } else {
      showToastNotification({
        type: 'error',
        description: t('page.error.error_global_ajax'),
      });
      return false;
    }
  };

  const onFavorite = async () => {
    const isFav = isFavorite;
    if (userLoggedIn) {
      setIsFavorite(!isFav);
      let response;
      if (isFav) {
        trackGAEvent('WatchlistRemoveBegin');
        response = await deleteFavorite(item.itemId, xsrfToken);
        const hasValidData = validateResoponse(response, isFav);
        if (hasValidData) {
          updateFeeds(item, feedType, false, feedPostCode);
        }
        trackGAEvent('WatchlistRemoveSuccess');
      } else {
        trackGAEvent('WatchlistAddBegin');
        response = await createFavorite(item.itemId, xsrfToken);
        const hasValidData = validateResoponse(response, isFav);
        if (hasValidData) {
          updateFeeds(item, feedType, true, feedPostCode);
        }
      }

      // actual update
      setIsFavorite(response.isFavorite);

      if (response.isFavorite) {
        setShowFavouriteInfoModal(true);
      }
    } else {
      setShowLoginDialog(true);
      // create favorite item in session storage
      setSessionStorageItem('FAVORITE_ITEM', item);
    }
  };

  const handleContextMenu = () => {
    const newUrl = `${item.url}${item.url.includes('?') ? '&' : '?'}correlationId=${item.correlationId}`;
    setUrl(newUrl);
  };
  return (
    <>
      <StructuredListing
        className={className}
        title={item.title}
        price={formatPrice(item.price, t)}
        imageSrc={formatImageUrl(item.picture?.url, ImageSize.Large, item.picture?.sizes)}
        imageLoading={imageLoading}
        decoding={imageDecoding}
        isMaskedLink={item.thinContent ? true : false}
        onHeart={onFavorite}
        isFavorite={isFavorite}
        showFav={true}
        favAriaLabel={
          isFavorite ? t('homepage.listing.favourite.ariaLabel.remove') : t('homepage.listing.favourite.ariaLabel.add')
        }
        data-testid="structured-listing"
        linkProps={{ href: url, onClick: () => onClick(item.correlationId), onContextMenu: handleContextMenu }}
      />
      {isFavouritesConnectEnabled && showFavouriteInfoModal && (
        <BuyerInfoModal
          onClose={() => setShowFavouriteInfoModal(false)}
          onTrack={(params) => {
            trackGAEvent(params.action, params.label, {
              // eslint-disable-next-line camelcase
              event_category: params.category,
            });
          }}
        />
      )}
    </>
  );
};

export default React.memo(FeedItem);
